import { useMemo } from "react";

import { cn } from "@/lib/utils";

import { StarIcon } from "./icons";
import type { EditorialReviewType } from "./types";

const NO_STAR_DOT_CLASS_NAME = "bg-dim-gray rounded-full size-1 md:size-2";

interface RatingStarsProps {
  rating?: string;
}

function RatingStars({ rating }: RatingStarsProps) {
  const stars = useMemo(() => {
    const stars: React.ReactNode[] = [];
    const [activeStarsStr = "0", allStarsStr = "0"] = (rating || "").split("/");
    let activeStars = Math.round(Number.parseFloat(activeStarsStr) || 0);
    const allStars = Number.parseInt(allStarsStr, 10) || 0;
    let goldStars = 0;

    if (activeStars > allStars) {
      goldStars = activeStars - allStars;
      activeStars = allStars;
    }

    if (allStars < 1 && goldStars < 1) {
      return stars;
    }

    const extraStars = allStars - activeStars;

    for (let a = 0; a < activeStars; a++) {
      stars.push(<StarIcon key={`a${a}`} />);
    }
    for (let e = 0; e < extraStars; e++) {
      stars.push(<StarIcon color="tuna.100" key={`e${e}`} />);
    }
    for (let g = 0; g < goldStars; g++) {
      stars.push(<StarIcon color="sunshade.200" key={`g${g}`} />);
    }

    return stars;
  }, [rating]);

  const hasStars = useMemo(() => !!stars.length, [stars]);

  return (
    <div
      className={cn(
        "-translate-1/2 absolute top-1/2 left-1/2 flex items-center bg-eerie-black",
        hasStars ? "gap-[5px] px-[7px]" : "gap-1 px-[9px] md:gap-1.5 md:px-2.5"
      )}
    >
      {hasStars ? (
        stars
      ) : (
        <>
          <div className={NO_STAR_DOT_CLASS_NAME} />
          <div className={NO_STAR_DOT_CLASS_NAME} />
          <div className={NO_STAR_DOT_CLASS_NAME} />
        </>
      )}
    </div>
  );
}

interface EditorialReviewProps {
  className?: string;
  review: EditorialReviewType;
}

export default function EditorialReview({
  className,
  review,
}: EditorialReviewProps) {
  return (
    <li
      className={cn(
        "relative shrink-0 rounded-[12px] border-transparent bg-eerie-black py-5 shadow-white/25 transition-[colors,shadow,border-radius] md:rounded-[15px] md:border md:py-4.5",
        "hover:rounded-[10px] hover:border-white/20 hover:shadow-0-0-20",
        className
      )}
    >
      <p className="text-center text-14/[1.71em] text-white md:text-16/[1.75em] md:text-pale-lavender">
        “{review.excerpt}“
      </p>
      <div className="relative my-5.5 h-px bg-dark-gray md:my-[29px] md:bg-white/20">
        <RatingStars rating={review.originalRating} />
      </div>
      <h6 className="text-center font-medium text-14 text-white leading-[1em] md:text-16">
        <a
          className="link-overlay"
          href={review.url}
          rel="noopener noreferrer"
          target="_blank"
        >
          {review.source?.sourceName}
        </a>
      </h6>
    </li>
  );
}
